/**
 * productTypes.service.ts
 *
 * Admin reads and writes for the signed-in account's product types
 * (`product_types`), plus the read-only lookups their form needs. The plain
 * list lives in inventory.service (`listProductTypes`), which the inventory
 * screens already use.
 *
 * Single account: apiClient sends X-Target-Company for the account in session
 * and the API scopes every call to it. Writes are gated by Admin Access on the
 * server.
 *
 * Product type names are unique per account (trimmed, case-insensitive),
 * deleted rows included: a taken name answers 422 DUPLICATE_PRODUCT_TYPE_NAME
 * with a sentence naming the clash.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export const DUPLICATE_PRODUCT_TYPE_NAME = 'DUPLICATE_PRODUCT_TYPE_NAME';

/** One product type as GET /product-types/{id} answers it (ProductTypeFormatter). */
export interface ProductTypeDetail {
  productTypeID: number;
  accountID?: number;
  productTypeName: string;
  description: string;
  /** "0" = Product, "1" = Credit. */
  productCredits: string | number;
  /** 0 = Appointment disbursal, 1 = Payment (retail) disbursal. */
  disburseType: number;
  /** product_categories ids (the read-only /product-categories-lookup list). */
  prodcategory: number[];
  /** Equivalent credit types (product type ids), paired with the quantities. */
  productTypeCreditID: number[];
  productTypeCreditQuantity: number[];
  /** Plan type ids. */
  associationID: number[];
}

/** The body the store/update endpoints take (their "frontend payload" keys). */
export interface ProductTypePayload {
  productTypeName: string;
  /** 0 = Product, 1 = Credit. */
  type: 0 | 1;
  disburseType: 0 | 1;
  description: string;
  categoryIds: number[];
  planTypeIds: number[];
  equivalentCredits: Array<{ productTypeId: number; quantity: number }>;
}

/** A product carrying this type, as GET /product-types/{id}/products lists it. */
export interface ProductTypeAttachedProduct {
  quoteItemCostID: number;
  name: string;
  description?: string | null;
  price: string | number | null;
  active: boolean;
  categoryName: string;
  availableInStore: boolean;
}

/** How a product type is attached to an appointment type. */
export type AttachedVia = 'required' | 'credit' | 'both';

export interface AttachedAppointmentType {
  appointmentTypeID: number;
  name: string;
  groupName: string;
  via: AttachedVia;
}

export interface PlanTypeOption {
  planTypeID: number;
  name: string;
  active: boolean;
}

function unwrap<T>(data: unknown): T {
  const d = (data as { data?: unknown })?.data ?? data;
  return d as T;
}

function asIds(value: unknown): number[] {
  return Array.isArray(value) ? value.map(Number).filter((n) => Number.isFinite(n) && n > 0) : [];
}

function normalizeDetail(raw: Record<string, unknown>): ProductTypeDetail {
  return {
    productTypeID: Number(raw.productTypeID),
    accountID: raw.accountID == null ? undefined : Number(raw.accountID),
    productTypeName: String(raw.productTypeName ?? ''),
    description: String(raw.description ?? ''),
    productCredits: (raw.productCredits as string | number) ?? '0',
    disburseType: Number(raw.disburseType ?? 0) === 1 ? 1 : 0,
    prodcategory: asIds(raw.prodcategory),
    productTypeCreditID: asIds(raw.productTypeCreditID),
    productTypeCreditQuantity: Array.isArray(raw.productTypeCreditQuantity)
      ? raw.productTypeCreditQuantity.map(Number)
      : [],
    associationID: asIds(raw.associationID),
  };
}

export async function getProductType(id: number): Promise<ProductTypeDetail> {
  const res = await apiClient.get<ApiResponse<Record<string, unknown>>>(`/product-types/${id}`);
  return normalizeDetail(unwrap(res.data));
}

export async function createProductType(payload: ProductTypePayload): Promise<ProductTypeDetail> {
  const res = await apiClient.post<ApiResponse<Record<string, unknown>>>('/product-types', payload);
  return normalizeDetail(unwrap(res.data));
}

export async function updateProductType(id: number, payload: ProductTypePayload): Promise<ProductTypeDetail> {
  const res = await apiClient.put<ApiResponse<Record<string, unknown>>>(`/product-types/${id}`, payload);
  return normalizeDetail(unwrap(res.data));
}

/** Soft delete (status 0). */
export async function deleteProductType(id: number): Promise<void> {
  await apiClient.delete(`/product-types/${id}`);
}

/** Refused with DUPLICATE_PRODUCT_TYPE_NAME when a live type already has the name. */
export async function restoreProductType(id: number): Promise<void> {
  await apiClient.post(`/product-types/${id}/restore`);
}

export async function listProductTypeProducts(id: number): Promise<ProductTypeAttachedProduct[]> {
  const res = await apiClient.get<ApiResponse<ProductTypeAttachedProduct[]>>(`/product-types/${id}/products`);
  return Array.isArray(res.data?.data) ? res.data.data : [];
}

export async function listPlanTypeOptions(): Promise<PlanTypeOption[]> {
  const res = await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/plan-types');
  const rows = Array.isArray(res.data?.data) ? res.data.data : [];
  return rows.map((r) => ({
    planTypeID: Number(r.planTypeID),
    name: String(r.name ?? ''),
    active: r.active === undefined ? true : r.active === true || r.active === 1 || r.active === '1',
  }));
}

/**
 * Each product type's default image (else its newest one), from one read of
 * the account's product-type images.
 */
export async function listProductTypeImageMap(): Promise<Map<number, string>> {
  const res = await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/product-image-urls', {
    params: { type: 'product-type' },
  });
  const rows = Array.isArray(res.data?.data) ? res.data.data : [];
  const map = new Map<number, string>();
  const isDefault = new Set<number>();
  for (const r of rows) {
    const id = Number(r.productTypeID);
    const url = String(r.productUrl ?? '').trim();
    if (!id || !url) continue;
    const def = Number(r.defaultUrl) === 1;
    if (def && !isDefault.has(id)) {
      map.set(id, url);
      isDefault.add(id);
    } else if (!map.has(id)) {
      map.set(id, url);
    }
  }
  return map;
}

/**
 * Each product type's product-category names, from one read of the account's
 * product type ↔ category links (GET /product-type-categories, with the
 * category embedded). Keyed by product type id. Admin > Product Types filters
 * by these names, as the corporate dashboard's Category filter does.
 */
export async function listProductTypeCategoryNames(): Promise<Map<number, string[]>> {
  const res = await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/product-type-categories');
  const rows = Array.isArray(res.data?.data) ? res.data.data : [];
  const byType = new Map<number, Set<string>>();
  for (const row of rows) {
    const typeId = Number(row.productTypeID ?? row.product_type_id ?? 0);
    const category = (row.product_category ?? row.productCategory ?? null) as Record<string, unknown> | null;
    const name = String(category?.productCategoryName ?? category?.title ?? '').trim();
    if (!typeId || !name) continue;
    let names = byType.get(typeId);
    if (!names) {
      names = new Set();
      byType.set(typeId, names);
    }
    names.add(name);
  }
  const out = new Map<number, string[]>();
  byType.forEach((names, typeId) => out.set(typeId, [...names]));
  return out;
}

/**
 * Which live appointment types use each product type, as a required product or
 * as their service credit. Keyed by product type id; one entry per appointment
 * type ("both" when it uses the type both ways). Same rule as the corporate
 * dashboard's product types page.
 */
export async function listAppointmentTypeAttachments(): Promise<Map<number, AttachedAppointmentType[]>> {
  const res = await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/appointment-types', {
    params: { active: true },
  });
  const rows = Array.isArray(res.data?.data) ? res.data.data : [];
  const byType = new Map<number, Map<number, AttachedAppointmentType>>();
  for (const at of rows) {
    if (at.active === false || at.active === 0 || at.deleted === true) continue;
    const atId = Number(at.appointmentTypeID ?? at.id ?? 0);
    const name = String(at.name ?? `Appointment type #${atId}`);
    const groupName = String(at.group_name ?? '');
    const uses: Array<{ ptId: number; via: AttachedVia }> = [];
    const required = Array.isArray(at.required_products) ? (at.required_products as Array<Record<string, unknown>>) : [];
    for (const rp of required) {
      const ptId = Number(rp.product_type_id);
      if (ptId) uses.push({ ptId, via: 'required' });
    }
    const creditId = Number(at.service_credit_id ?? 0);
    if (creditId) uses.push({ ptId: creditId, via: 'credit' });
    for (const { ptId, via } of uses) {
      let entries = byType.get(ptId);
      if (!entries) {
        entries = new Map();
        byType.set(ptId, entries);
      }
      const existing = entries.get(atId);
      if (!existing) entries.set(atId, { appointmentTypeID: atId, name, groupName, via });
      else if (existing.via !== via) existing.via = 'both';
    }
  }
  const out = new Map<number, AttachedAppointmentType[]>();
  byType.forEach((entries, ptId) => out.set(ptId, [...entries.values()]));
  return out;
}
